// 📄 configuracionGeneral.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ArrowRight, Save, RefreshCw, AlertTriangle, Cpu, Network, Layout, KeyRound, LifeBuoy, Building2 } from 'lucide-react';
import { useGeneralConfig, tabLabel } from './useGeneralConfig.ts';
import { useChecklistIncidentAlerts } from './useChecklistIncidentAlerts.ts';
import { useProject } from '../../../../context/ProjectContext.tsx';
import { buildChecklistStepKey } from '../../../../data/commerceChecklistCatalog.ts';
import ChecklistIncidentBanner from '../../../Proyectos/components/ChecklistIncidentBanner.tsx';
import CustomDevelopmentSection from '../../../../components/integracion/CustomDevelopmentSection.tsx';
import { emptyCustomDevelopment } from '../../../../types/customDevelopment.ts';
import EnvironmentTabs from '../../../../components/integracion/EnvironmentTabs.tsx';
import TransportConfirmationPanel from '../../../../components/integracion/TransportConfirmationPanel.tsx';
import { emptyTransportConfirmation } from '../../../../types/transportConfirmation.ts';
import SRSection from './components/SRSection.tsx';

// Importación de componentes de pasos ERP (on-premise: ECC / S/4HANA On-Premise)
import { StepCertificates } from './components/StepCertificates.tsx';
import { StepRFC } from './components/StepRFC.tsx';
import { StepLogicalSystem } from './components/StepLogicalSystem.tsx';
import { StepActivation } from './components/StepActivation.tsx';
import { StepPortDefinition } from './components/StepPortDefinition.tsx';

// Sub-fase K8: los Step de ERP Cloud (Usuario de Comunicación/System ID/
// Communication Arrangement/CIG URL/Scenario/Test) ya NO se importan ni se
// renderizan acá — el tab "erp" en Cloud ahora muestra un cartel que
// redirige al Repositorio de Comunicación (K5), fuente única para ese caso.
// Los componentes StepCloud*.tsx quedan en el repo sin uso (datos viejos
// intactos en Firestore, ver useGeneralConfig.ts) por si hiciera falta
// consultarlos como referencia histórica.

// Importación de componentes de pasos Cloud Connector
import { StepCCSubaccount } from './components/StepCCSubaccount.tsx';
import { StepCCVirtualHost } from './components/StepCCVirtualHost.tsx';
import { StepCCResources } from './components/StepCCResources.tsx';
import { StepCCTunnel } from './components/StepCCTunnel.tsx';

// Importación de componentes de pasos Ariba Portal
import { StepAPSiteConfig } from './components/StepAPSiteConfig.tsx';
import { StepAPChannels } from './components/StepAPChannels.tsx';
import { StepAPTesting } from './components/StepAPTesting.tsx';

// Importación de componentes de pasos Managed Gateway (CIG)
import { StepMGProject } from './components/StepMGProject.tsx';
import { StepMGConnections } from './components/StepMGConnections.tsx';
import { StepMGGlobalMapping } from './components/StepMGGlobalMapping.tsx';

// Metadatos de visualización de todos los pasos
const STEP_METADATA: Record<string, { title: string; desc: string }> = {
  // ERP
  certificates: { title: '1. Certificados SSL', desc: 'Verificación de llaves públicas en la STRUST' },
  rfc: { title: '2. Conexión RFC / HTTP', desc: 'Configurar destino RFC hacia Ariba - CIG' },
  port: { title: '3. Port Definition', desc: 'Crear el puerto lógico (WE21)' },
  logicalsystem: { title: '4. Logical System', desc: 'Creación del sistema lógico (BD54)' },
  activation: { title: '5. Activación de Canales', desc: 'Habilitar eventos y triggers de documentos' },
  // ERP Cloud: sub-fase K8 movió su UI al Repositorio de Comunicación —
  // ya no hay steps con id cloud_* en TAB_STEPS (ver useGeneralConfig.ts).
  // Cloud Connector
  cc_subaccount: { title: '1. Add Subaccount (BTP Setup)', desc: 'Región, Subaccount ID y código Passcode' },
  cc_virtual_host: { title: '2. Cloud to On-Premise Mapping', desc: 'Back-end ABAP System, HTTPS, Virtual/Internal Host-Port' },
  cc_resources: { title: '3. Access Control (Resources)', desc: 'Habilitar rutas /sap y sub-paths entrantes/salientes' },
  cc_tunnel: { title: '4. Estado del Túnel (Handshake)', desc: 'Verificación de conectividad SCC ↔ Subaccount BTP' },
  // Ariba Portal
  ap_site_config: { title: '1. Parámetros del Sitio', desc: 'Vincular Realm ID e Integration Directory' },
  ap_channels: { title: '2. Switches de Canales', desc: 'Enrutamiento de PO, ASN, Invoice, Receipt' },
  ap_testing: { title: '3. Test de Conectividad', desc: 'Handshake y ping del portal hacia CIG' },
  // Managed Gateway
  mg_project: { title: '1. Proyecto CIG', desc: 'Crear proyecto, Realm y System ID' },
  mg_connections: { title: '2. Conexiones SOAP/REST', desc: 'Setup de endpoints de integración' },
  mg_global_mapping: { title: '3. Mapeos Globales', desc: 'Cross-Referencing de Company Codes' }
};

export default function GeneralConfig() {
  const { esCliente, projectId } = useProject();
  const {
    activeTab,
    setActiveTab,
    activeStep,
    setActiveStep,
    completedSteps,
    formData,
    handleFieldChange,
    requiresOTMap,
    setRequiresOTMap,
    otNumberMap,
    setOtNumberMap,
    customDevMap,
    setCustomDevMap,
    qasTransportMap,
    setQasTransportMap,
    prdTransportMap,
    setPrdTransportMap,
    qasManualMap,
    prdManualMap,
    confirmingTransport,
    handleConfirmTransport,
    validationError,
    setValidationError,
    saveStatus,
    loadingData,
    testingConnection,
    connectionStatus,
    handleTestConnection,
    handleSave,
    selectedEnv,
    handleEnvChange,
    tabSteps,
    esCloudErp
  } = useGeneralConfig();

  const incidentAlerts = useChecklistIncidentAlerts(projectId, selectedEnv === 'PRD' && activeTab !== 'sr');
  const activeStepIncidente = activeTab !== 'sr' ? incidentAlerts[buildChecklistStepKey('generals', activeStep, activeTab)] : undefined;

  if (loadingData) {
    return (
      <div className="p-8 text-center text-gray-400 flex flex-col items-center justify-center space-y-3 h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <p className="text-sm">Sincronizando entorno de configuración con OneHub Cloud...</p>
      </div>
    );
  }

  // Pasos correspondientes al Tab seleccionado (el de "erp" cambia según
  // si el proyecto usa un ERP on-premise o S/4HANA Cloud — ver esCloudErp)
  const currentTabSteps = tabSteps[activeTab] || [];

  const isDevView = selectedEnv === 'DEV';
  const stepRequiresOT = requiresOTMap[activeStep] || false;
  const showTransportPanel = !isDevView && stepRequiresOT;
  const showEnvEditableSave = !isDevView && !stepRequiresOT;

  return (
    <div className="p-2 text-white max-w-6xl mx-auto space-y-6">
      {/* 📑 BARRA DE PESTAÑAS HORIZONTAL (TABS DE PLATAFORMA) */}
      <div className="flex flex-wrap bg-[rgb(var(--c-surface)/0.6)] border border-white/10 rounded-2xl p-1.5 gap-1.5 shadow-xl">
        {(['erp', 'cloudconnector', 'ariba', 'gateway', 'sr'] as const).map((tab) => {
          const isActive = activeTab === tab;
          let accentClass = '';

          if (tab === 'erp') accentClass = isActive ? 'bg-blue-500/15 text-blue-400 border border-blue-500/30' : 'text-gray-400 hover:text-white';
          if (tab === 'cloudconnector') accentClass = isActive ? 'bg-indigo-500/15 text-indigo-400 border border-indigo-500/30' : 'text-gray-400 hover:text-white';
          if (tab === 'ariba') accentClass = isActive ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30' : 'text-gray-400 hover:text-white';
          if (tab === 'gateway') accentClass = isActive ? 'bg-purple-500/15 text-purple-400 border border-purple-500/30' : 'text-gray-400 hover:text-white';
          if (tab === 'sr') accentClass = isActive ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30' : 'text-gray-400 hover:text-white';

          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-xs font-bold tracking-wide uppercase transition-all duration-200 ${accentClass}`}
            >
              {tab === 'erp' && <Layout className="w-4 h-4" />}
              {tab === 'cloudconnector' && <Network className="w-4 h-4" />}
              {tab === 'ariba' && <Cpu className="w-4 h-4" />}
              {tab === 'gateway' && <KeyRound className="w-4 h-4" />}
              {tab === 'sr' && <LifeBuoy className="w-4 h-4" />}
              {tabLabel(tab, esCloudErp)}
            </button>
          );
        })}
      </div>

      {activeTab === 'sr' && <SRSection />}

      {/* Sub-fase K8: en Cloud, el tab "erp" ya no tiene su propio wizard —
          apunta al Repositorio de Comunicación (K5), fuente única de
          Acceso al Tenant + el pool de Communication Users/Systems. */}
      {activeTab === 'erp' && esCloudErp && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-10 text-center space-y-4">
          <Building2 className="w-10 h-10 mx-auto text-blue-400" />
          <h3 className="text-lg font-semibold text-white">Acceso al Tenant se gestiona desde el Repositorio de Comunicación</h3>
          <p className="text-sm text-gray-400 max-w-lg mx-auto">
            En proyectos S/4HANA Cloud, la URL del tenant, los Scopes habilitados y el pool de Communication Users/Systems viven en una pantalla dedicada, no acá.
          </p>
          {projectId && (
            <Link
              to={`/proyecto/${projectId}/repositorio-comunicacion`}
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all"
            >
              Ir al Repositorio de Comunicación
            </Link>
          )}
        </div>
      )}

      {activeTab !== 'sr' && !(activeTab === 'erp' && esCloudErp) && (
      <>
      {validationError && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl flex items-center gap-3 text-sm animate-fadeIn">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{validationError}</span>
        </div>
      )}

      {activeStepIncidente && projectId && (
        <ChecklistIncidentBanner projectId={projectId} incidente={activeStepIncidente} />
      )}

      {saveStatus === 'success' && (
        <div className="bg-green-500/10 border border-green-500/30 text-green-400 p-4 rounded-xl text-sm">
          🎉 ¡Configuración guardada y paso registrado con éxito en la plataforma!
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* COLUMNA 1: El Checklist */}
        <div className="lg:col-span-1 bg-white/5 border border-white/10 rounded-2xl p-4 h-fit space-y-3 shadow-lg">
          <div className="pb-2 border-b border-white/10">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider px-2">Progreso de Integración</h3>
            <p className="text-[11px] text-gray-500 px-2 mt-0.5">
              Plataforma activa: {tabLabel(activeTab, esCloudErp)}
              {activeTab === 'erp' && !esCloudErp && ' · On-Premise'}
            </p>
          </div>
          <div className="space-y-2">
            {currentTabSteps.map((stepId) => {
              const metadata = STEP_METADATA[stepId] || { title: stepId, desc: '' };
              const isCompleted = completedSteps.includes(stepId);
              const isActive = activeStep === stepId;

              // Color del check y active según el tab
              let themeClass = 'text-blue-400';
              let fillClass = 'fill-blue-500';
              if (activeTab === 'cloudconnector') { themeClass = 'text-indigo-400'; fillClass = 'fill-indigo-500'; }
              if (activeTab === 'ariba') { themeClass = 'text-amber-400'; fillClass = 'fill-amber-500'; }
              if (activeTab === 'gateway') { themeClass = 'text-purple-400'; fillClass = 'fill-purple-500'; }

              return (
                <div
                  key={stepId}
                  onClick={() => {
                    setActiveStep(stepId);
                    setValidationError(null);
                  }}
                  className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all border ${
                    isActive
                      ? activeTab === 'erp'
                        ? 'bg-blue-500/10 border-blue-500 text-white font-medium'
                        : activeTab === 'cloudconnector'
                          ? 'bg-indigo-500/10 border-indigo-500 text-white font-medium'
                          : activeTab === 'ariba'
                            ? 'bg-amber-500/10 border-amber-500 text-white font-medium'
                            : 'bg-purple-500/10 border-purple-500 text-white font-medium'
                      : 'bg-transparent border-transparent hover:bg-white/5 text-gray-300'
                  }`}
                >
                  <div className={`mt-0.5 ${themeClass}`}>
                    {isCompleted ? (
                      <CheckCircle2 className={`w-5 h-5 ${fillClass} text-slate-900`} />
                    ) : (
                      <Circle className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium ${isCompleted && !isActive ? 'line-through text-gray-500' : ''}`}>{metadata.title}</p>
                    <p className="text-xs text-gray-400 truncate mt-0.5">{metadata.desc}</p>
                  </div>
                  {isActive && <ArrowRight className={`w-4 h-4 ${themeClass} self-center`} />}
                </div>
              );
            })}
          </div>

          <div className="pt-2 px-2">
            <div className="flex justify-between text-xs text-gray-400 mb-1">
              <span>Pasos completados</span>
              <span>{completedSteps.length} de {currentTabSteps.length}</span>
            </div>
            <div className="w-full bg-white/10 h-1.5 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  activeTab === 'erp' ? 'bg-blue-500' : activeTab === 'cloudconnector' ? 'bg-indigo-500' : activeTab === 'ariba' ? 'bg-amber-500' : 'bg-purple-500'
                }`}
                style={{ width: `${(completedSteps.length / currentTabSteps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* COLUMNA 2 y 3 */}
        <div className="lg:col-span-2 bg-white/5 border border-white/10 rounded-2xl p-6 flex flex-col min-h-[480px] shadow-lg min-w-0">
          {/* SELECTOR DE AMBIENTE GLOBAL — fuera del fieldset para que
              siempre se pueda cambiar de ambiente, incluso en modo cliente. */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-white/10 pb-4 mb-6 gap-3">
            <div>
              <span className="text-xs font-semibold text-blue-400 uppercase tracking-wider">Configuración Activa</span>
              <h4 className="text-sm text-gray-400">
                {isDevView ? 'Estás editando' : showTransportPanel ? 'Estás consultando (heredado de DEV)' : 'Estás editando (independiente por ambiente)'} los parámetros de la pestaña en:
              </h4>
            </div>
            <EnvironmentTabs
              selectedEnv={selectedEnv}
              onChange={handleEnvChange}
              devConfigured={completedSteps.includes(activeStep)}
              stepRequiresOT={stepRequiresOT}
              qas={(stepRequiresOT ? qasTransportMap : qasManualMap)[activeStep] || emptyTransportConfirmation()}
              prd={(stepRequiresOT ? prdTransportMap : prdManualMap)[activeStep] || emptyTransportConfirmation()}
            />
          </div>

          {/* Envoltorio flex-col justify-between con SIEMPRE 2 hijos (campos
              del paso + bloque inferior), cada uno en su propio fieldset,
              para que el panel de Confirmación de Transporte nunca quede
              anidado dentro del fieldset que deshabilita los campos. */}
          <div className="flex-1 flex flex-col justify-between">
            <fieldset disabled={esCliente || showTransportPanel}>
              {/* Renderizado dinámico de los pasos de cada plataforma */}
              {activeTab === 'erp' && activeStep === 'certificates' && (
                <StepCertificates dataCenter={formData.dataCenter} onChange={handleFieldChange} />
              )}
              {activeTab === 'erp' && activeStep === 'rfc' && (
                <StepRFC
                  rfcDestination={formData.rfcDestination}
                  connectionType={formData.connectionType}
                  onChange={handleFieldChange}
                  testingConnection={testingConnection}
                  connectionStatus={connectionStatus}
                  onTestConnection={handleTestConnection}
                  isInvalid={!!validationError}
                />
              )}
              {activeTab === 'erp' && activeStep === 'port' && (
                <StepPortDefinition onChange={handleFieldChange} />
              )}
              {activeTab === 'erp' && activeStep === 'logicalsystem' && (
                <StepLogicalSystem
                  realmId={formData.realmId}
                  apiClientId={formData.apiClientId}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeTab === 'erp' && activeStep === 'activation' && (
                <StepActivation />
              )}

              {/* Cloud Connector Step Components */}
              {activeTab === 'cloudconnector' && activeStep === 'cc_subaccount' && (
                <StepCCSubaccount
                  ccBtpRegion={formData.ccBtpRegion}
                  ccLocationId={formData.ccLocationId}
                  ccDisplayName={formData.ccDisplayName}
                  ccSelectedSubaccountId={formData.ccSelectedSubaccountId}
                  ccCompletedSubaccounts={formData.ccCompletedSubaccounts}
                  selectedEnv={selectedEnv}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeTab === 'cloudconnector' && activeStep === 'cc_virtual_host' && (
                <StepCCVirtualHost
                  ccMasterHost={formData.ccMasterHost}
                  ccVirtualHost={formData.ccVirtualHost}
                  ccMasterPort={formData.ccMasterPort}
                  ccVirtualPort={formData.ccVirtualPort}
                  ccProtocol={formData.ccProtocol}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeTab === 'cloudconnector' && activeStep === 'cc_resources' && (
                <StepCCResources
                  ccResourcePath={formData.ccResourcePath}
                  ccResourceEnabled={formData.ccResourceEnabled}
                  onChange={handleFieldChange}
                />
              )}
              {activeTab === 'cloudconnector' && activeStep === 'cc_tunnel' && (
                <StepCCTunnel
                  ccTunnelSubaccount={formData.ccTunnelSubaccount}
                  ccTunnelStatus={formData.ccTunnelStatus}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}

              {/* Ariba Portal Step Components */}
              {activeTab === 'ariba' && activeStep === 'ap_site_config' && (
                <StepAPSiteConfig
                  apRealmId={formData.apRealmId}
                  apIntegrationDir={formData.apIntegrationDir}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeTab === 'ariba' && activeStep === 'ap_channels' && (
                <StepAPChannels
                  apEnablePO={formData.apEnablePO}
                  apEnableASN={formData.apEnableASN}
                  apEnableInvoice={formData.apEnableInvoice}
                  apEnableReceipt={formData.apEnableReceipt}
                  onChange={handleFieldChange}
                />
              )}
              {activeTab === 'ariba' && activeStep === 'ap_testing' && (
                <StepAPTesting
                  apTestTargetUrl={formData.apTestTargetUrl}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}

              {/* Managed Gateway Step Components */}
              {activeTab === 'gateway' && activeStep === 'mg_project' && (
                <StepMGProject
                  mgProjectId={formData.mgProjectId}
                  mgSystemId={formData.mgSystemId}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeTab === 'gateway' && activeStep === 'mg_connections' && (
                <StepMGConnections
                  mgEndpointUrl={formData.mgEndpointUrl}
                  mgAuthType={formData.mgAuthType}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
              {activeTab === 'gateway' && activeStep === 'mg_global_mapping' && (
                <StepMGGlobalMapping
                  mgOrgId={formData.mgOrgId}
                  mgCompanyCode={formData.mgCompanyCode}
                  onChange={handleFieldChange}
                  isInvalid={!!validationError}
                />
              )}
            </fieldset>

            {isDevView && (
              <fieldset disabled={esCliente} className="border-t border-white/10 pt-4 mt-6 space-y-4">
                {activeTab !== 'cloudconnector' && (
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={requiresOTMap[activeStep] || false}
                          onChange={(e) => {
                            setRequiresOTMap({ ...requiresOTMap, [activeStep]: e.target.checked });
                            setValidationError(null);
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-9 h-5 bg-zinc-700 rounded-full peer peer-checked:after:translate-x-full peer-checked:bg-blue-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                      </label>
                      <div>
                        <span className="text-xs font-medium block">¿Requiere Orden de Transporte (OT)?</span>
                        <span className="text-[10px] text-gray-400">Actívalo si estás guardando cambios en un mandante modificable.</span>
                      </div>
                    </div>

                    {(requiresOTMap[activeStep] || false) && (
                      <div className="flex-1 max-w-xs animate-fadeIn">
                        <input
                          type="text"
                          value={otNumberMap[activeStep] || ''}
                          onChange={(e) => setOtNumberMap({ ...otNumberMap, [activeStep]: e.target.value.toUpperCase() })}
                          maxLength={10}
                          placeholder="ej. DEVK900123"
                          className="w-full bg-zinc-800 border border-blue-500/30 text-blue-400 rounded-xl px-3 py-2 text-sm font-mono uppercase tracking-wider text-center focus:outline-none"
                        />
                      </div>
                    )}
                  </div>
                )}

                <CustomDevelopmentSection
                  value={customDevMap[activeStep] || emptyCustomDevelopment()}
                  onChange={(data) => setCustomDevMap({ ...customDevMap, [activeStep]: data })}
                />

                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Comentarios / Bitácora del Consultor Técnico</label>
                  <textarea
                    value={formData.comments}
                    onChange={(e) => handleFieldChange('comments', e.target.value)}
                    placeholder="Aclaraciones sobre este paso en el ambiente de DEV..."
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none transition-all"
                  />
                </div>

                {!esCliente && (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saveStatus === 'saving'}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg text-slate-950 ${
                        activeTab === 'erp'
                          ? 'bg-blue-500 hover:bg-blue-600'
                          : activeTab === 'cloudconnector'
                            ? 'bg-indigo-500 hover:bg-indigo-600'
                            : activeTab === 'ariba'
                              ? 'bg-amber-500 hover:bg-amber-600'
                              : 'bg-purple-500 hover:bg-purple-600'
                      } disabled:bg-gray-600`}
                    >
                      <Save className="w-4 h-4" />
                      {saveStatus === 'saving' ? 'Guardando...' : 'Guardar Ajustes en DEV'}
                    </button>
                  </div>
                )}
              </fieldset>
            )}

            {showEnvEditableSave && (
              <fieldset disabled={esCliente} className="border-t border-white/10 pt-4 mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1">Comentarios / Bitácora del Consultor Técnico ({selectedEnv})</label>
                  <textarea
                    value={formData.comments}
                    onChange={(e) => handleFieldChange('comments', e.target.value)}
                    placeholder={`Aclaraciones sobre este paso en el ambiente de ${selectedEnv}...`}
                    rows={2}
                    className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 resize-none transition-all"
                  />
                </div>

                {!esCliente && (
                  <div className="flex justify-end pt-2">
                    <button
                      onClick={handleSave}
                      disabled={saveStatus === 'saving'}
                      className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-lg text-slate-950 ${
                        selectedEnv === 'PRD' ? 'bg-red-500 hover:bg-red-600' : 'bg-orange-500 hover:bg-orange-600'
                      } disabled:bg-gray-600`}
                    >
                      <Save className="w-4 h-4" />
                      {saveStatus === 'saving' ? 'Guardando...' : `Guardar Ajustes en ${selectedEnv}`}
                    </button>
                  </div>
                )}
              </fieldset>
            )}

            {showTransportPanel && (
              <fieldset disabled={esCliente}>
                <TransportConfirmationPanel
                  env={selectedEnv as 'QAS' | 'PRD'}
                  value={(selectedEnv === 'QAS' ? qasTransportMap : prdTransportMap)[activeStep] || emptyTransportConfirmation()}
                  onChange={(data) => {
                    if (selectedEnv === 'QAS') setQasTransportMap({ ...qasTransportMap, [activeStep]: data });
                    else setPrdTransportMap({ ...prdTransportMap, [activeStep]: data });
                  }}
                  onConfirm={() => handleConfirmTransport(selectedEnv as 'QAS' | 'PRD')}
                  saving={confirmingTransport}
                />
              </fieldset>
            )}
          </div>
        </div>
      </div>
      </>
      )}
    </div>
  );
}
